/**
 * Session Health Monitoring System
 * Coordinates all session management enhancements for comprehensive stale session handling
 */

import { ref, onMounted, onUnmounted } from 'vue';
import { initializeGlobalIdleManager, destroyGlobalIdleManager, getGlobalIdleManager, type IdleManagerDebugInfo } from '@/services/idle-manager';
import { useCrossTabSync } from '@/composables/useCrossTabSync';
import { RootCauseDetector } from '@/utils/root-cause-detector';
import { useApplicationStore } from '@/store/application.store';
import { useUserStore } from '@/store/user.store';
import { router } from '@/router';
import { validateSessionWithRefresh, resetInFlightValidations } from '@/store/helpers/authenticate/tokens';
import { useCentralizedDemo } from '@/composables/useCentralizedDemo';
import { SESSION_EXPIRED_DEDUPE_KEY, SESSION_EXPIRED_DEDUPE_TTL } from '@/constants/notifications';

export interface SessionHealthConfig {
  // Idle management
  idleTimeout?: number;
  warningTimeout?: number;
  enableIdleManagement?: boolean;
  
  // Idle warning - defaults to false (modern apps use silent token refresh)
  // Only enable if you want old-school "you will be logged out" warnings
  showIdleWarning?: boolean;
  
  // Cross-tab sync
  enableCrossTabSync?: boolean;
  
  // Root cause detection
  enableRootCauseDetection?: boolean;
  
  // Session validation
  sessionValidationInterval?: number;
  enablePeriodicValidation?: boolean;
  
  // Development options
  enableInDevelopment?: boolean;
  debugMode?: boolean;
}

export function useSessionHealthMonitoring(config: SessionHealthConfig = {}) {
  const isInitialized = ref(false);
  const isMonitoring = ref(false);
  const debugMode = config.debugMode ?? false;
  const { isDemoMode } = useCentralizedDemo();
  
  // Default configuration
  const defaultConfig: Required<SessionHealthConfig> = {
    idleTimeout: 15 * 60 * 1000, // 15 minutes
    warningTimeout: 13 * 60 * 1000, // 13 minutes (only used if showIdleWarning is true)
    enableIdleManagement: true,
    showIdleWarning: false, // Modern apps use silent token refresh - no premature warnings
    enableCrossTabSync: true,
    enableRootCauseDetection: true,
    sessionValidationInterval: 10 * 60 * 1000, // 10 minutes
    enablePeriodicValidation: true,
    enableInDevelopment: false,
    debugMode: false
  };
  
  const finalConfig = { ...defaultConfig, ...config };
  
  // Component references
  let crossTabSync: ReturnType<typeof useCrossTabSync> | null = null;
  let rootCauseDetector: RootCauseDetector | null = null;
  let periodicValidationInterval: NodeJS.Timeout | null = null;
  let resumeHandler: (() => void) | null = null;

  /**
   * Log debug information if debug mode is enabled
   */
  const debugLog = (message: string, ...args: any[]) => {
    if (debugMode) {
      console.log(`[SessionHealthMonitoring] ${message}`, ...args);
    }
  };

  /**
   * Handle idle state - user has been inactive
   */
  const handleIdle = async () => {
    if (isDemoMode.value) return; // No sessions in demo
    debugLog('User idle detected');
    
    const applicationStore = useApplicationStore();
    const userStore = useUserStore();
    
    if (!userStore.isAuthenticated) {
      return;
    }
    
    try {
      // Validate session when user becomes idle
      const sessionValidation = await validateSessionWithRefresh();
      
      if (!sessionValidation.valid) {
        debugLog('Session invalid during idle check, logging out');
        
        applicationStore.showNotification({
          title: 'Session Expired',
          message: 'Your session expired while you were away. Please log in again.',
          theme: 'warning',
          autoClose: false,
          persistent: true,
          isOpen: true,
          dedupeKey: SESSION_EXPIRED_DEDUPE_KEY,
          dedupeWindowMs: SESSION_EXPIRED_DEDUPE_TTL
        });
        
        // Force logout
        setTimeout(async () => {
          try {
            const { navigateWithLoadingState } = await import('@/utils/navigation');
            const { NAME_LOGIN } = await import('@/router/routes/constants');
            await navigateWithLoadingState({ name: NAME_LOGIN });
          } catch {
            router.push('/login');
          }
        }, 2000);
        
      } else {
        if (sessionValidation.refreshed) {
          debugLog('Session refreshed during idle check');
          
          applicationStore.showNotification({
            title: 'Session Renewed',
            message: 'Your session has been automatically renewed.',
            theme: 'success',
            autoClose: true,
            timeout: 4000,
            isOpen: true
          });
        }
      }
      
    } catch (error) {
      debugLog('Error during idle session validation:', error);
    }
  };

  /**
   * Handle idle warning - warn user before they become idle
   * NOTE: Disabled by default (showIdleWarning: false) - modern apps use silent token refresh
   * Only enable for enterprise/banking apps that require explicit idle warnings
   */
  const handleIdleWarning = () => {
    if (isDemoMode.value) return; // Suppress warnings in demo
    if (!finalConfig.showIdleWarning) return; // Skip if warnings disabled
    debugLog('Idle warning triggered');
    
    const applicationStore = useApplicationStore();
    
    applicationStore.showNotification({
      title: 'Session Warning',
      message: 'You will be logged out soon due to inactivity. Please interact with the page to stay logged in.',
      theme: 'warning',
      autoClose: true,
      timeout: 10000,
      isOpen: true
    });
  };

  /**
   * Handle user activity after idle period
   */
  const handleActivityAfterIdle = async () => {
    if (isDemoMode.value) return; // No checks in demo
    debugLog('User activity detected after idle period');
    
    const userStore = useUserStore();
    
    if (userStore.isAuthenticated) {
      try {
        // Validate session when user becomes active again
        const sessionValidation = await validateSessionWithRefresh();
        
        if (!sessionValidation.valid) {
          debugLog('Session invalid after idle recovery');
          
          const applicationStore = useApplicationStore();
          applicationStore.showNotification({
            title: 'Session Expired',
            message: 'Your session expired while you were away. Please log in again.',
            theme: 'warning',
            autoClose: false,
            persistent: true,
            isOpen: true,
            dedupeKey: SESSION_EXPIRED_DEDUPE_KEY,
            dedupeWindowMs: SESSION_EXPIRED_DEDUPE_TTL
          });
          
          setTimeout(async () => {
            try {
              const { navigateWithLoadingState } = await import('@/utils/navigation');
              const { NAME_LOGIN } = await import('@/router/routes/constants');
              await navigateWithLoadingState({ name: NAME_LOGIN });
            } catch {
              router.push('/login');
            }
          }, 2000);
        }
        
      } catch (error) {
        debugLog('Error validating session after idle recovery:', error);
      }
    }
  };

  /**
   * Handle session expiration from idle manager
   */
  const handleSessionExpired = async () => {
    if (isDemoMode.value) {
      debugLog('Demo mode active - suppressing session expiration handling');
      return;
    }
    debugLog('Session expiration detected by idle manager');
    
    // The idle manager already handles logout, but this provides a fallback
    try {
      const { useAuthenticateStore } = await import('@/store/authenticate.store');
      const authenticateStore = useAuthenticateStore();
      
      // Check if user is still authenticated (in case logout wasn't called)
      const userStore = useUserStore();
      if (userStore.isAuthenticated) {
        debugLog('User still authenticated, calling logout as fallback');
        await authenticateStore.logout();
      } else {
        debugLog('User already logged out, just ensuring redirect');
        setTimeout(async () => {
          try {
            const { navigateWithLoadingState } = await import('@/utils/navigation');
            const { NAME_LOGIN } = await import('@/router/routes/constants');
            await navigateWithLoadingState({ name: NAME_LOGIN });
          } catch {
            router.push('/login');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('[SessionHealthMonitoring] Fallback session expiration error:', error);
      // Final fallback: just redirect
      setTimeout(async () => {
        try {
          const { navigateWithLoadingState } = await import('@/utils/navigation');
          const { NAME_LOGIN } = await import('@/router/routes/constants');
          await navigateWithLoadingState({ name: NAME_LOGIN });
        } catch {
          router.push('/login');
        }
      }, 2000);
    }
  };

  /**
   * Handle session refresh from idle manager
   */
  const handleSessionRefreshed = () => {
    if (isDemoMode.value) return; // Nothing to do in demo
    debugLog('Session refresh detected by idle manager');
    
    // Signal refresh to other tabs
    if (crossTabSync) {
      crossTabSync.signalTokenRefresh();
    }
  };

  /**
   * Perform periodic session validation
   */
  const performPeriodicValidation = async () => {
    if (isDemoMode.value) return; // Skip periodic validation in demo
    const userStore = useUserStore();
    
    if (!userStore.isAuthenticated) {
      return;
    }

    try {
      debugLog('Performing periodic session validation');
      
      const sessionValidation = await validateSessionWithRefresh();
      
      if (!sessionValidation.valid) {
        debugLog('Periodic session validation failed, initiating proper logout');
        
        const applicationStore = useApplicationStore();
        const currentRoute = router.currentRoute.value;
        const isOnLoginPage = currentRoute.name === 'login' || currentRoute.path === '/login';
        
        applicationStore.showNotification({
          title: 'Session Expired',
          message: isOnLoginPage 
            ? 'Your session has expired. Please log in again.'
            : 'Your session has expired. You will be redirected to login.',
          theme: 'warning',
          autoClose: false,
          persistent: true,
          isOpen: true,
          dedupeKey: SESSION_EXPIRED_DEDUPE_KEY,
          dedupeWindowMs: SESSION_EXPIRED_DEDUPE_TTL
        });
        
        // Import and call proper logout function
        try {
          const { useAuthenticateStore } = await import('@/store/authenticate.store');
          const authenticateStore = useAuthenticateStore();
          await authenticateStore.logout();
        } catch (error) {
          console.error('[SessionHealthMonitoring] Error during logout:', error);
          // Fallback: manual cleanup
          userStore.setState({ key: 'isAuthenticated', value: false });
          setTimeout(async () => {
            try {
              const { navigateWithLoadingState } = await import('@/utils/navigation');
              const { NAME_LOGIN } = await import('@/router/routes/constants');
              await navigateWithLoadingState({ name: NAME_LOGIN });
            } catch {
              router.push('/login');
            }
          }, 2000);
        }
        
      } else if (sessionValidation.refreshed) {
        debugLog('Session refreshed during periodic validation');
        
        // Signal refresh to other tabs
        if (crossTabSync) {
          crossTabSync.signalTokenRefresh();
        }
      }
      
    } catch (error) {
      debugLog('Error during periodic session validation:', error);
    }
  };

  /**
   * Initialize all session monitoring components
   */
  const initialize = async () => {
    if (isInitialized.value) {
      debugLog('Already initialized, skipping...');
      return;
    }

    // In demo mode we disable all session handling and user notifications
    if (isDemoMode.value) {
      debugLog('Demo mode detected - disabling session health monitoring');
      // Ensure any existing global idle manager is destroyed
      try { destroyGlobalIdleManager(); } catch {}
      isInitialized.value = true;
      isMonitoring.value = false;
      return;
    }

    debugLog('Initializing session health monitoring with config:', finalConfig);

    try {
      // Initialize idle management
      if (finalConfig.enableIdleManagement) {
        debugLog('Initializing idle management (showIdleWarning:', finalConfig.showIdleWarning, ')');
        
        initializeGlobalIdleManager(
          handleIdle,
          // Only pass warning handler if explicitly enabled - modern apps use silent refresh
          finalConfig.showIdleWarning ? handleIdleWarning : undefined,
          handleActivityAfterIdle,
          {
            idleTimeout: finalConfig.idleTimeout,
            warningTimeout: finalConfig.warningTimeout,
            enableInDevelopment: finalConfig.enableInDevelopment,
            sessionValidationInterval: finalConfig.sessionValidationInterval,
            enableSessionValidation: true,
            onSessionExpired: handleSessionExpired,
            onSessionRefreshed: handleSessionRefreshed
          }
        );
      }

      // Initialize cross-tab synchronization
      if (finalConfig.enableCrossTabSync) {
        debugLog('Initializing cross-tab synchronization');
        
        crossTabSync = useCrossTabSync({
          enabled: true,
          syncInterval: 30000
        });
      }

      // Initialize root cause detection
      if (finalConfig.enableRootCauseDetection) {
        debugLog('Initializing root cause detection');
        
        rootCauseDetector = RootCauseDetector.getInstance();
        rootCauseDetector.startMonitoring();
      }

      // Initialize periodic session validation
      if (finalConfig.enablePeriodicValidation) {
        debugLog('Starting periodic session validation');
        
        periodicValidationInterval = setInterval(
          performPeriodicValidation,
          finalConfig.sessionValidationInterval
        );
      }

      // Add visibility/focus listeners to reset any stuck validations and trigger quick health checks
      try {
        resumeHandler = () => {
          debugLog('Tab resumed (focus/visibility), resetting in-flight validations');
          try { resetInFlightValidations(); } catch {}
          // Lightweight nudge: no await to avoid blocking UI thread
          validateSessionWithRefresh(false).catch(() => {});
        };
        const onVisibility = () => { if (!document.hidden && resumeHandler) resumeHandler(); };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', resumeHandler);
        // Store wrapper to remove later
        (resumeHandler as any)._onVisibility = onVisibility;
      } catch {}

      isInitialized.value = true;
      isMonitoring.value = true;
      
      debugLog('Session health monitoring initialization complete');

    } catch (error) {
      console.error('[SessionHealthMonitoring] Initialization failed:', error);
      throw error;
    }
  };

  /**
   * Cleanup all monitoring components
   */
  const cleanup = () => {
    debugLog('Cleaning up session health monitoring');

    try {
      // Cleanup idle management
      if (finalConfig.enableIdleManagement) {
        destroyGlobalIdleManager();
      }

      // Cleanup cross-tab sync
      if (crossTabSync) {
        crossTabSync.cleanup();
        crossTabSync = null;
      }

      // Cleanup root cause detection
      if (rootCauseDetector) {
        rootCauseDetector.stopMonitoring();
        rootCauseDetector = null;
      }

      // Cleanup periodic validation
      if (periodicValidationInterval) {
        clearInterval(periodicValidationInterval);
        periodicValidationInterval = null;
      }

      try {
        if (resumeHandler) {
          window.removeEventListener('focus', resumeHandler);
          const onVisibility = (resumeHandler as any)._onVisibility;
          if (onVisibility) {
            document.removeEventListener('visibilitychange', onVisibility);
          }
          resumeHandler = null;
        }
      } catch {}

      isInitialized.value = false;
      isMonitoring.value = false;
      
      debugLog('Session health monitoring cleanup complete');

    } catch (error) {
      console.error('[SessionHealthMonitoring] Cleanup failed:', error);
    }
  };

  /**
   * Get current monitoring status
   */
  const getStatus = () => {
    const idleManager = getGlobalIdleManager();
    
    return {
      isInitialized: isInitialized.value,
      isMonitoring: isMonitoring.value,
      config: finalConfig,
      components: {
        idleManager: !!idleManager,
        crossTabSync: !!crossTabSync,
        rootCauseDetector: !!rootCauseDetector,
        periodicValidation: !!periodicValidationInterval
      },
      idleManagerDebug: idleManager?.getDebugInfo() as IdleManagerDebugInfo | null
    };
  };

  /**
   * Force a session health check
   */
  const forceHealthCheck = async () => {
    debugLog('Forcing session health check');
    
    try {
      // Trigger idle manager session check
      const idleManager = getGlobalIdleManager();
      if (idleManager) {
        await idleManager.forceSessionCheck();
      }
      
      // Trigger periodic validation
      await performPeriodicValidation();
      
      debugLog('Forced session health check completed');
      
    } catch (error) {
      console.error('[SessionHealthMonitoring] Forced health check failed:', error);
    }
  };

  // Auto-initialize if enabled
  onMounted(() => {
    const isDevelopment = import.meta.env.DEV;
    const shouldInitialize = !isDevelopment || finalConfig.enableInDevelopment;
    
    if (shouldInitialize) {
      initialize();
    } else {
      debugLog('Skipping initialization in development mode');
    }
  });

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup();
  });

  return {
    isInitialized,
    isMonitoring,
    initialize,
    cleanup,
    getStatus,
    forceHealthCheck,
    config: finalConfig
  };
}
